import { useCallback, useEffect, useMemo, useState } from "react";
import { hasChildren } from "@gb/contracts";

/**
 * Which groups of an outline of phases are folded, by a key that survives
 * reordering (2026-10-03). The outline's rules and moves are in
 * `@gb/contracts` (`phase-outline.ts`), shared with the backoffice.
 *
 * A group that arrives already a group is folded: the phases loaded from the
 * server, or a programme just read. A programme of ten sections and sixty
 * tasks then reads as ten rows, and a person opens the one they want.
 *
 * A group a person makes, by indenting a row under one already on screen,
 * stays open. It used to fold the moment it became a group, because the hook
 * folded the first groups it saw whatever made them: the row just indented
 * disappeared from under the person's hand (found by the branch review of
 * 2026-10-03).
 */
export function useFoldedGroups<T extends { level: number }>(
  rows: readonly T[],
  keyOf: (row: T) => string,
) {
  // Every key seen so far, and the groups folded.
  const [state, setState] = useState<{ known: Set<string>; folded: Set<string> }>(() => ({
    known: new Set(),
    folded: new Set(),
  }));

  // The folded groups as of this render: those already folded, and any group
  // whose key has not been seen before. Read now, so a new list does not flash
  // open before the effect below stores it.
  const folded = useMemo(() => {
    const next = new Set(state.folded);
    rows.forEach((row, index) => {
      const key = keyOf(row);
      if (!state.known.has(key) && hasChildren(rows, index)) next.add(key);
    });
    return next;
  }, [rows, state, keyOf]);

  useEffect(() => {
    if (rows.every((row) => state.known.has(keyOf(row)))) return;
    setState({ known: new Set([...state.known, ...rows.map(keyOf)]), folded });
  }, [rows, state, folded, keyOf]);

  const toggle = useCallback(
    (key: string) =>
      setState((before) => {
        const next = new Set(folded);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return { ...before, folded: next };
      }),
    [folded],
  );

  /** Open every group holding one of these rows, so a row with an error is seen. */
  const reveal = useCallback(
    (indexes: readonly number[]) =>
      setState((before) => {
        const next = new Set(folded);
        for (const index of indexes) {
          let level = rows[index]?.level ?? 0;
          for (let i = index - 1; i >= 0 && level > 0; i -= 1) {
            if (rows[i]!.level < level) {
              next.delete(keyOf(rows[i]!));
              level = rows[i]!.level;
            }
          }
        }
        return { ...before, folded: next };
      }),
    [rows, folded, keyOf],
  );

  /** The rows on screen: every row not inside a folded group. */
  const visible = useMemo(() => {
    const shown: number[] = [];
    let hiddenBelow: number | null = null;
    rows.forEach((row, index) => {
      if (hiddenBelow !== null && row.level > hiddenBelow) return;
      hiddenBelow = null;
      shown.push(index);
      if (folded.has(keyOf(row)) && hasChildren(rows, index)) hiddenBelow = row.level;
    });
    return shown;
  }, [rows, folded, keyOf]);

  return {
    visible,
    isFolded: (row: T) => folded.has(keyOf(row)),
    toggle,
    reveal,
  };
}
